import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './rootReducer';
import { Nullable } from '../common/types';
import { updateSearchParams } from '../common/utils';

// initialState
export type LogsInitialState = {
  logNodes: string[];
  modalLogNodes: string[];
  logsContent: Record<string, string>[];
  activeLogNode: Nullable<string>;
  logStartTime: Nullable<string>;
  logEndTime: Nullable<string>;
  selectedNodesForDownload: string[];
  selectedStartDateForDownload: Date | null;
  selectedEndDateForDownload: Date | null;
  downloadError: DownloadError | null;
  logsDownloadURL: string | null;
  downloadInstanceId: string | null;
  isLoading: boolean;
  isDownloading: boolean;
  isButtonDisabled: boolean;
  selectedLogType?: AgvLogType;
  isModalLoading: boolean;
};

export type AgvLogType = 'event' | 'periodical';

type DownloadError = {
  downloadingBlocked: boolean;
  message: string;
};

type LogContentResPayload = {
  preceding: boolean;
  content: string;
  logStartTime: string;
};

export const initialState: LogsInitialState = {
  logNodes: [],
  modalLogNodes: [],
  logsContent: [],
  activeLogNode: null,
  logStartTime: null,
  logEndTime: null,
  selectedNodesForDownload: [],
  selectedStartDateForDownload: null,
  selectedEndDateForDownload: null,
  downloadError: null,
  logsDownloadURL: null,
  downloadInstanceId: null,
  isLoading: false,
  isModalLoading: false,
  isDownloading: false,
  isButtonDisabled: false,
  selectedLogType: 'event',
};

// createSlice
export const logsSlice = createSlice({
  name: 'logs',
  initialState,
  reducers: {
    setLogNodes: (state, action: PayloadAction<string[]>) => {
      state.logNodes = action.payload;
      state.activeLogNode =
        action.payload.length > 0 && !state.activeLogNode
          ? action.payload[0]
          : state.activeLogNode;
      state.isLoading = false;

      if (!state.activeLogNode) {
        updateSearchParams('logNode', '', true);
      }
    },
    setModalLogNodes: (state, action: PayloadAction<string[]>) => {
      state.modalLogNodes = action.payload;
      state.isLoading = false;
      state.isModalLoading = false;
    },
    setActiveLogNode(state, action: PayloadAction<string | null>) {
      state.activeLogNode = action.payload;
    },
    setLogDateTimeAndResetNodes(
      state,
      action: PayloadAction<{
        startDate: Nullable<string>;
        endDate?: Nullable<string>;
      }>,
    ) {
      const { startDate, endDate } = action.payload;

      if (
        (state.logStartTime === startDate && state.logEndTime === endDate) ||
        (state.logStartTime === startDate && state.logEndTime === null) // <- For Astronaut
      ) {
        return;
      }

      state.logStartTime = startDate;
      if (endDate) state.logEndTime = endDate;
      state.logNodes = [];
      state.logsContent = [];
    },
    setLogDateTime(
      state,
      action: PayloadAction<{
        startDate: Nullable<string>;
        endDate?: Nullable<string>;
      }>,
    ) {
      const { startDate, endDate } = action.payload;

      if (state.logStartTime === startDate && state.logEndTime === endDate) {
        return;
      }
      state.logStartTime = startDate;
      if (endDate) state.logEndTime = endDate;
    },
    setLogContent(state, action: PayloadAction<LogContentResPayload>) {
      const { logStartTime, preceding, content } = action.payload;
      const newEntry = { [logStartTime]: content };
      const updatedContent = preceding
        ? [newEntry, ...state.logsContent]
        : [...state.logsContent, newEntry];

      const uniqueContent = getUniqueObjectsByKey(updatedContent);
      state.logsContent = preceding
        ? uniqueContent.slice(0, 2)
        : uniqueContent.length > 2
        ? uniqueContent.slice(1)
        : uniqueContent;
    },
    setSelectedNodesForDownload(state, action: PayloadAction<string[]>) {
      state.selectedNodesForDownload = action.payload;
    },
    setSelectedStartDateForDownload(state, action: PayloadAction<Date | null>) {
      state.selectedStartDateForDownload = action.payload;
    },
    setSelectedEndDateForDownload(state, action: PayloadAction<Date | null>) {
      state.selectedEndDateForDownload = action.payload;
    },
    setDownloadError(state, action: PayloadAction<DownloadError | null>) {
      state.downloadError = action.payload;
    },
    setLogsDownloadURL(
      state,
      action: PayloadAction<{
        instanceId: string | null;
        logsDownloadURL: string | null;
      }>,
    ) {
      const { instanceId, logsDownloadURL } = action.payload;
      state.downloadInstanceId = instanceId;
      state.logsDownloadURL = logsDownloadURL;
      state.isDownloading = false;
    },
    resetLogContent(state) {
      state.logsContent = [];
    },
    setLoadingState(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setModalLoadingState(state, action: PayloadAction<boolean>) {
      state.isModalLoading = action.payload;
    },
    setDownloadingState(state, action: PayloadAction<boolean>) {
      state.isDownloading = action.payload;
    },
    setDisabledButton(state, action: PayloadAction<boolean>) {
      state.isButtonDisabled = action.payload;
    },
    setSelectedLogType(state, action: PayloadAction<AgvLogType>) {
      state.selectedLogType = action.payload;
    },
    resetLogsState: () => initialState,
  },
});

export const {
  setLogNodes,
  setActiveLogNode,
  setLogDateTime,
  setLogDateTimeAndResetNodes,
  setLogContent,
  setSelectedNodesForDownload,
  setSelectedStartDateForDownload,
  setSelectedEndDateForDownload,
  setDownloadError,
  setLogsDownloadURL,
  resetLogContent,
  resetLogsState,
  setModalLogNodes,
  setLoadingState,
  setDownloadingState,
  setDisabledButton,
  setSelectedLogType,
  setModalLoadingState,
} = logsSlice.actions;

export const logsSelectors = {
  selectStatus: (state: RootState) => state.logs.logNodes,
  selectModalLogNodes: (state: RootState) => state.logs.modalLogNodes,
  selectActiveLogNode: (state: RootState) => state.logs.activeLogNode,
  selectLogStartTime: (state: RootState) => state.logs.logStartTime,
  selectLogEndTime: (state: RootState) => state.logs.logEndTime,
  selectSelectedLogType: (state: RootState) => state.logs.selectedLogType,
  selectLogsContent: (state: RootState) => state.logs.logsContent,
  selectedNodesForDownload: (state: RootState) =>
    state.logs.selectedNodesForDownload,
  selectedStartDateForDownload: (state: RootState) =>
    state.logs.selectedStartDateForDownload,
  selectedEndDateForDownload: (state: RootState) =>
    state.logs.selectedEndDateForDownload,
  selectedDownloadURL: (state: RootState) => state.logs.logsDownloadURL,
  selectedDownloadInstanceId: (state: RootState) =>
    state.logs.downloadInstanceId,
  selectLoadingState: (state: RootState) => state.logs.isLoading,
  selectModalLoadingState: (state: RootState) => state.logs.isModalLoading,
  selectDownloadingState: (state: RootState) => state.logs.isDownloading,

  downloadError: (state: RootState) => state.logs.downloadError,
  selectDisabledButtonState: (state: RootState) => state.logs.isButtonDisabled,
  selectLogContentStartDate: (state: RootState) => {
    const content = state.logs.logsContent;

    const result: {
      contentFirstLogDate: string | null;
      contentSecondLogDate: string | null;
    } = {
      contentFirstLogDate: null,
      contentSecondLogDate: null,
    };

    if (content.length >= 1) {
      result.contentFirstLogDate = Object.keys(content[0])[0];
    }
    if (content.length >= 2) {
      result.contentSecondLogDate = Object.keys(content[1])[0];
    }

    return result;
  },
};

export const getLogNodesAction = createAction<{
  entityId: string;
  startDate: string;
  endDate?: string;
  LogsTypeName?: string;
}>('logs/getLogNodes');

export const getModalLogNodesAction = createAction<{
  entityId: string;
  startDate: string;
  endDate: string;
  LogsTypeName?: string;
}>('logs/getModalLogNodes');

export const getLogContentAction = createAction<{
  entityId: string;
  logFileName: string;
  startDate: string;
  preceding: boolean;
}>('logs/getLogContent');

export const downloadLogFiles = createAction<{
  entityId: string;
  startDate: Date;
  endDate: Date;
  fileNames: string[];
  instanceId: string;
}>('logs/downloadLogFiles');

function getUniqueObjectsByKey(array: Record<string, string>[]) {
  const keys = new Set(); // to remember keys we've encountered
  const result: Record<string, string>[] = []; // the unique objects we return

  for (const obj of array) {
    // Get the key of the object (assuming always one key per object)
    const key = Object.keys(obj)[0];

    if (!keys.has(key)) {
      keys.add(key);
      result.push(obj); // add first occurrence of this key to result
    }
  }

  return result;
}
export default logsSlice.reducer;
